// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { AttachmentOwnerType as AttachmentOwnerTypeValues } from './attachment.js';
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `event_type` a pack's deadline row declares to say "this framework has a
 * marketing-communications consent and suppression register". Generic on
 * purpose: any instrument that makes per-channel marketing consent and
 * suppression load-bearing can declare it, in any jurisdiction.
 */
export const MARKETING_COMMUNICATIONS_EVENT_TYPE = 'marketing_communications_lifecycle';
/** `register_surface` values pack forms declare to feed these registers' vocabularies. */
export const MARKETING_CONSENT_REGISTER_SURFACE = 'marketing_consent';
export const MARKETING_SUPPRESSION_REGISTER_SURFACE = 'marketing_suppression';
export const MARKETING_SUPPRESSION_ENFORCEMENT_REGISTER_SURFACE = 'marketing_suppression_enforcement';
/**
 * The form field ids whose `options[]` the vocabulary reader collects.
 *
 * `communication_channel` is NOT `consent_channel`. Sprint 62.24.0 already
 * spent `consent_channel` on the FORMAT of a consent artefact
 * (`credit-information-supply.ts:42` maps it to `consent_formats`), and a
 * reader who saw the same field id mean "SMS" on one register and "signed
 * paper" on another would be right to distrust both. The two vocabularies stay
 * separate field ids and a pack may declare both.
 */
export const MARKETING_CONSENT_VOCABULARY_FIELDS = {
    channels: 'communication_channel',
    consentSources: 'marketing_consent_source',
    noticeKinds: 'marketing_notice_kind',
};
export const MARKETING_SUPPRESSION_VOCABULARY_FIELDS = {
    channels: 'communication_channel',
    suppressionReasons: 'marketing_suppression_reason',
    confirmationChannels: 'marketing_confirmation_channel',
};
export const MARKETING_SUPPRESSION_ENFORCEMENT_VOCABULARY_FIELDS = {
    channels: 'communication_channel',
    senderIdentityKinds: 'marketing_sender_identity_kind',
};
/** The boundary tag these registers read. */
export const MARKETING_COMMUNICATIONS_BOUNDARY_TAG = 'marketing_communications_boundary';
/**
 * The option-level flag a pack sets on a CHANNEL option to say "sending on
 * this channel requires the subject's prior agreement". Read strictly: only
 * the boolean `true` flags a channel. Which channels carry it is the pack's
 * statement, never the platform's (R-46) — and a pack may well declare a
 * channel with the flag absent, which means the pack is silent, not that
 * consent is unnecessary.
 */
export const PACK_OPTION_PRIOR_CONSENT_REQUIRED = 'requires_prior_consent';
/**
 * The option-level flag a pack sets on a CHANNEL option to say "sending on
 * this channel requires a registered sender identity". Same strict reading.
 */
export const PACK_OPTION_SENDER_IDENTITY_REQUIRED = 'requires_sender_identity';
/**
 * The option-level flag a pack sets on a SUPPRESSION-REASON option to say "the
 * register behind this reason is maintained by an authority, not by the
 * organisation". The platform never holds such a register's contents; a check
 * against it is a fact a person records, and its absence reads
 * `no_authority_register_declared` rather than `not_reconciled`, because those
 * are different sentences.
 */
export const PACK_OPTION_AUTHORITY_MAINTAINED_REGISTER = 'authority_maintained_register';
/**
 * Where the proof of the agreement is, including the case where there is none.
 *
 * A marketing consent entry does NOT require an artefact, and that is a
 * deliberate departure from the credit-information consent register, which does.
 * The reasoning is the population: an instrument that makes each consent
 * individually reproducible on demand is served by requiring the artefact, but
 * an organisation holding a million channel opt-ins does not attach a million
 * files, and a register that refused the record would be answered by a fake
 * attachment rather than by better evidence. So absence is RECORDED — the
 * register shows which entries rest on nothing — instead of refused.
 */
export const MARKETING_CONSENT_EVIDENCE_STATES = [
    'held_here',
    'in_custody',
    'not_recorded',
    /**
     * Sprint 62.25.0 AC14 (D-AC14) — the entry names an artefact that is no
     * longer held here: it was deleted, or is no longer owned by the entry.
     * Re-checked at read with the write check's own predicate, never stored.
     * Before this, `held_here` was derived from the attachment id's presence
     * alone, so a deleted file still read as held.
     */
    'no_longer_held',
];
/**
 * Whether the entry still authorises sending, and if not, what ended it.
 *
 * `suppressed` wins over `withdrawn` only in the sense of being reported
 * first; both are stated rather than collapsed, because a subject who withdrew
 * consent AND was suppressed for a complaint has two facts about them and an
 * inspector will ask about each. `not_assessed` is what a failed read of the
 * linked workflow or the suppression register produces — never `active`, and
 * this is the Sprint 61.0.11 rule applied here: a page may not state that
 * something is absent when the read that would have told it failed.
 */
export const MARKETING_CONSENT_STANDING_STATES = [
    'active',
    'withdrawn',
    'suppressed',
    'not_assessed',
];
/**
 * What the suppression covers. `all_channels` is a distinct recorded scope
 * rather than a row per channel, because a subject who says "stop contacting
 * me" said one thing, and fanning it out into N rows would make a later
 * channel added to the pack silently uncovered.
 */
export const SUPPRESSION_SCOPES = ['single_channel', 'all_channels'];
/**
 * Whether the organisation has done what a suppression asks. Derived at read
 * time from the row, never stored.
 *
 * `confirmation_not_recorded` is the interesting state: the subject was
 * suppressed but there is no record that they were told. `stopped_after_request`
 * means `effective_from` is later than `requested_at` — the row states its own
 * delay rather than hiding it.
 */
export const SUPPRESSION_COMPLETENESS_STATES = [
    'enforced_and_confirmed',
    'confirmation_not_recorded',
    'stopped_after_request',
    'lifted',
];
/**
 * Sprint 62.25.0 AC14 (D-AC14) — whether the confirmation artefact a
 * suppression entry names is still held here, re-checked at read with the write
 * check's predicate (`attachmentsStillHeld`). `null` when the entry has no
 * confirmation or the confirmation names no artefact. Never stored.
 */
export const SUPPRESSION_CONFIRMATION_ARTEFACT_STATES = ['held_here', 'no_longer_held'];
// ── Suppression-enforcement check ──
/**
 * How the send population was decided. `automated` does not mean "an AI did
 * it"; it means a system, not a person, selected or scored the recipients, and
 * it is the trigger for the assessment below.
 */
export const ENFORCEMENT_METHODS = ['manual', 'automated'];
/**
 * Whether the authority-maintained register was reconciled.
 *
 * Three states, not a boolean, and the third is the point:
 * `no_authority_register_declared` means no installed pack flags any
 * suppression reason with `PACK_OPTION_AUTHORITY_MAINTAINED_REGISTER`, so
 * there is no such register for this tenant to reconcile against — which is
 * not the same sentence as "the organisation did not reconcile".
 */
export const AUTHORITY_REGISTER_RECONCILIATION_STATES = [
    'reconciled',
    'not_reconciled',
    'no_authority_register_declared',
];
/**
 * What the check leaves open. Derived at read time.
 *
 * `sender_identity_not_recorded` fires only when the channel's own pack option
 * carries `requires_sender_identity`; on a channel no pack flags, an absent
 * sender identity is not a gap and is not reported as one.
 */
export const ENFORCEMENT_CHECK_GAPS = [
    'automated_without_human_review',
    'automated_without_false_positive_review',
    'sender_identity_not_recorded',
    'sender_identity_registration_expired',
    'authority_register_not_reconciled',
];
// ── Collections ──
/**
 * The attachment owner type a held-here consent copy is uploaded under.
 *
 * Derived from `AttachmentOwnerType` rather than written out again, so the page
 * that uploads and the service that verifies the binding cannot drift — the
 * defect class this repository has recorded on a hand-copied allowlist, a
 * hand-copied record-type list and a hand-copied collection name.
 */
export const MARKETING_CONSENT_EVIDENCE_OWNER_TYPE = AttachmentOwnerTypeValues.MARKETING_CONSENT_EVIDENCE;
export const MARKETING_CONSENT_COLLECTION = 'grc_marketing_consent_entries';
export const MARKETING_SUPPRESSION_COLLECTION = 'grc_marketing_suppression_entries';
export const MARKETING_SUPPRESSION_ENFORCEMENT_COLLECTION = 'grc_marketing_suppression_checks';
// ── Error codes ──
export const MARKETING_COMMUNICATIONS_ERROR_CODES = {
    /** A value outside the installed pack vocabulary. */
    VOCABULARY_VALUE_UNKNOWN: 'VOCABULARY_VALUE_UNKNOWN',
    /**
     * A named attachment is not held for this organisation. NOT
     * "an artefact is required": a marketing consent entry may legitimately rest
     * on no artefact at all, and `evidence_state: not_recorded` says so. This
     * fires only when an id was supplied and does not resolve.
     */
    ARTEFACT_NOT_HELD: 'ARTEFACT_NOT_HELD',
    /**
     * Sprint 62.28.0 — the attachment exists for this organisation but is not
     * bound to THIS entry. Distinct from `ARTEFACT_NOT_HELD`, which is "no such
     * file": "someone else's proof" and "no proof" are different sentences, and a
     * register whose rows can borrow each other's evidence is not a register.
     */
    ARTEFACT_NOT_BOUND_TO_ENTRY: 'ARTEFACT_NOT_BOUND_TO_ENTRY',
    /**
     * An attachment cannot be named when the entry is created, because the entry
     * it would be bound to does not exist yet. Evidence held here is recorded by
     * PATCH once it does — the owner ruling on idea-catalog #304. The refusal is
     * explicit rather than a silent drop, so a client that tries learns why.
     */
    ARTEFACT_NOT_BINDABLE_AT_CREATE: 'ARTEFACT_NOT_BINDABLE_AT_CREATE',
    /** A notice reference must carry a governed policy or a version label. */
    NOTICE_VERSION_REQUIRED: 'NOTICE_VERSION_REQUIRED',
    /** `scope: single_channel` with no channel, or `all_channels` with one. */
    SUPPRESSION_SCOPE_CHANNEL_MISMATCH: 'SUPPRESSION_SCOPE_CHANNEL_MISMATCH',
    /** The same subject and channel already carry a standing suppression. */
    SUPPRESSION_ALREADY_STANDING: 'SUPPRESSION_ALREADY_STANDING',
    /** A lift was requested on a suppression that is already lifted. */
    SUPPRESSION_ALREADY_LIFTED: 'SUPPRESSION_ALREADY_LIFTED',
    /**
     * A confirmation was recorded on a suppression that already carries one — a
     * confirmation is recorded once. Was a backend-local literal until fix sprint
     * 20260922-2142 (F33); the wire value is unchanged.
     */
    SUPPRESSION_ALREADY_CONFIRMED: 'SUPPRESSION_ALREADY_CONFIRMED',
    /** `suppressed_count` exceeds `population_size`. */
    SUPPRESSED_COUNT_EXCEEDS_POPULATION: 'SUPPRESSED_COUNT_EXCEEDS_POPULATION',
    /** `false_positive_count` exceeds `sample_size`. */
    FALSE_POSITIVE_COUNT_EXCEEDS_SAMPLE: 'FALSE_POSITIVE_COUNT_EXCEEDS_SAMPLE',
    /** An automated-classification assessment on a `manual` check, or absent on an `automated` one. */
    CLASSIFICATION_METHOD_MISMATCH: 'CLASSIFICATION_METHOD_MISMATCH',
    /**
     * No installed pack declares these registers. Not `NO_…`: the R-46 lint
     * reads a jurisdiction-prefixed SCREAMING_SNAKE literal as a framework code,
     * and `NO` is one.
     */
    REGISTER_NOT_DECLARED: 'REGISTER_NOT_DECLARED',
};
//# sourceMappingURL=marketing-communications.js.map